// PDF pipeline: greedy block-level pagination (marginTop injection) → html2canvas snapshot → slice at page boundaries → pdf-lib.
// Principle: if it renders in the editor, it renders in the PDF (html2canvas captures live DOM styles).

interface ExportToPdfOptions {
    target: HTMLElement;
    filename?: string;
    /**
     * When true, skips `planPages` and emits ONE tall PDF page covering the
     * full editor content. Trades off pagination correctness (no widow/orphan
     * handling, no sticky-heading logic — there's just no boundary to fight)
     * against viewer compatibility (Adobe Acrobat caps page height at
     * 14400 pt ≈ 200 in; many viewers handle more but it varies). Use for
     * "give me the whole thing as a scroll" exports where pagination is
     * actively unwanted.
     */
    singlePage?: boolean;
}

// A3 portrait — wider than A4 so the 1229px editor content isn't crushed.
const PAGE_WIDTH_PT = 842;
const MARGIN_PT = 24;
const SNAPSHOT_SCALE = 3; // N=3 for sharp text; safe up to ~5400 CSS px tall documents.
const PDF_CONTENT_WIDTH_PT = PAGE_WIDTH_PT - MARGIN_PT * 2;
const PDF_PAGE_ASPECT = 1143 / PDF_CONTENT_WIDTH_PT;
const PAGE_TOP_PADDING_CSS = 48; // ~0.5in inner page padding
const PAGE_BOTTOM_PADDING_CSS = 48;

export function getDefaultPageHeightPx(contentWidthPx: number): number {
    return contentWidthPx * PDF_PAGE_ASPECT;
}

interface InlineStyleBackup {
    el: HTMLElement;
    overflow: string;
    overflowY: string;
    maxHeight: string;
    height: string;
}

function expandForCapture(target: HTMLElement): InlineStyleBackup[] {
    const backups: InlineStyleBackup[] = [];
    const contentEl = target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null;
    const chain: HTMLElement[] = [];
    if (contentEl) chain.push(contentEl);
    let node: HTMLElement | null = contentEl?.parentElement ?? null;
    while (node && node !== target) {
        chain.push(node);
        node = node.parentElement;
    }
    if (target) chain.push(target);

    for (const el of chain) {
        backups.push({
            el,
            overflow: el.style.overflow,
            overflowY: el.style.overflowY,
            maxHeight: el.style.maxHeight,
            height: el.style.height,
        });
        el.style.overflow = 'visible';
        el.style.overflowY = 'visible';
        el.style.maxHeight = 'none';
        el.style.height = 'auto';
    }
    return backups;
}

function restoreAfterCapture(backups: InlineStyleBackup[]) {
    for (const b of backups) {
        b.el.style.overflow = b.overflow;
        b.el.style.overflowY = b.overflowY;
        b.el.style.maxHeight = b.maxHeight;
        b.el.style.height = b.height;
    }
}

// Presigned S3 URLs lack CORS headers — fetch bytes ourselves and use data: URLs.
async function urlToDataUrl(url: string): Promise<string> {
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const blob = await res.blob();
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Resolve data-doc-id images to base64 via presigned URLs (CORS workaround).
async function resolveDocIdImagesToBase64(contentEl: HTMLElement): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    const docIdSet = new Set<string>();
    contentEl.querySelectorAll<HTMLImageElement>('img[data-doc-id]').forEach((img) => {
        const id = img.getAttribute('data-doc-id');
        if (id) docIdSet.add(id);
    });
    if (docIdSet.size === 0) return map;

    const helpers = await import('../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers');
    const docIds = Array.from(docIdSet)
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));
    if (docIds.length === 0) return map;

    const bulk = await helpers.getPresignedUrlBulk(docIds);
    const failures: string[] = [];
    await Promise.all(
        docIds.map(async (idNum) => {
            const presignedUrl = bulk[idNum];
            if (!presignedUrl) {
                failures.push(`docId=${idNum} (no presigned URL returned)`);
                return;
            }
            try {
                const dataUrl = await urlToDataUrl(presignedUrl);
                map.set(String(idNum), dataUrl);
            } catch (err: any) {
                failures.push(`docId=${idNum}: ${String(err?.message || err)}`);
            }
        }),
    );
    if (failures.length) {
        // eslint-disable-next-line no-console
        console.warn('[exportToPdf] docId image resolution failures', failures);
    }
    return map;
}

async function preloadImagesAsDataUrls(contentEl: HTMLElement): Promise<Map<string, string>> {
    const imgs = Array.from(contentEl.querySelectorAll('img')) as HTMLImageElement[];
    const unique = new Set<string>();
    imgs.forEach((img) => {
        const src = img.getAttribute('src') || img.src;
        if (src && /^https?:/i.test(src)) unique.add(src);
    });
    const map = new Map<string, string>();
    const failures: Array<{ url: string; error: string }> = [];
    await Promise.all(
        Array.from(unique).map(async (url) => {
            try {
                map.set(url, await urlToDataUrl(url));
            } catch (err: any) {
                failures.push({ url: url.slice(0, 100), error: String(err?.message || err) });
            }
        }),
    );
    if (failures.length) {
        // eslint-disable-next-line no-console
        console.warn('[exportToPdf] image preload failures', failures);
    }
    return map;
}

interface LinkRect {
    url: string;
    /** Coords in CANVAS pixels, relative to the snapshot canvas's top-left. */
    x: number;
    y: number;
    w: number;
    h: number;
}

function collectLinks(contentEl: HTMLElement): LinkRect[] {
    const links: LinkRect[] = [];
    const contentRect = contentEl.getBoundingClientRect();
    contentEl.querySelectorAll('a[href]').forEach((node) => {
        const a = node as HTMLAnchorElement;
        const href = a.href;
        if (!href || href === '#' || href.startsWith('javascript:')) return;
        const rects = a.getClientRects();
        Array.from(rects).forEach((r) => {
            links.push({
                url: href,
                x: (r.left - contentRect.left) * SNAPSHOT_SCALE,
                y: (r.top - contentRect.top) * SNAPSHOT_SCALE,
                w: r.width * SNAPSHOT_SCALE,
                h: r.height * SNAPSHOT_SCALE,
            });
        });
    });
    return links;
}

/** A computed page range in CSS px relative to `contentEl`'s top edge. */
interface PageRange {
    topCSS: number;
    heightCSS: number;
}

/** A reversible marginTop edit applied to a block element. */
interface MarginEdit {
    el: HTMLElement;
    previous: string;
    /** Optional reversible `display` change. When set, restoreMarginEdits also
     *  restores `display` along with `marginTop`. Used to temporarily hide
     *  trailing empty paragraphs so the rasterized canvas doesn't carry their
     *  whitespace into the PDF as a blank page. */
    previousDisplay?: string;
}

// Uses offsetTop (not summed offsetHeight) because CSS margins between blocks would silently drift computed boundaries.
// Headings stick with the next block to prevent orphan titles at page bottoms.
const STICKY_PRECEDING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
// Walk back at most this many blocks looking for a heading to pull along. A
// limit is needed so we don't drag an entire previous section forward when the
// heading was many blocks back.
const MAX_STICKY_LOOKBACK = 8;
// Block types that visually break a sticky group when found between heading
// and table — we don't drag a sibling table or media along with another table.
const GROUP_BREAK_TAGS = new Set(['TABLE', 'HR', 'IMG', 'FIGURE']);
// Tags that TERMINATE a sticky group (inclusive) during the group-building pass.
// Includes UL/OL so heading + bulleted-list pairs receive the same orphan
// protection as heading + table.
const GROUP_END_TAGS = new Set(['TABLE', 'HR', 'IMG', 'FIGURE', 'UL', 'OL']);

function isStickyPrecedingBlock(block: HTMLElement): boolean {
    if (STICKY_PRECEDING_TAGS.has(block.tagName)) return true;
    const slateType = block.getAttribute('data-slate-type') || '';
    return (
        slateType === 'h1' ||
        slateType === 'h2' ||
        slateType === 'h3' ||
        slateType === 'h4' ||
        slateType === 'h5' ||
        slateType === 'h6' ||
        slateType === 'heading'
    );
}

interface PaginationGroup {
    startIdx: number;
    endIdx: number; // inclusive
}

// Build sticky groups from the block list before the pagination loop.
// A group is either:
//   • a sticky heading (h1-h6) + following blocks up to and including the next
//     GROUP_END_TAG or the start of the next heading (whichever comes first), OR
//   • a single standalone block (paragraph, plain content).
// Groups are capped at MAX_STICKY_LOOKBACK + 1 blocks so we never drag an
// entire previous section forward. This pre-pass replaces findStickyGroupStart
// / canPullGroup: the heading is part of its group by construction, so the
// "heading is first on page" guard that caused the orphaned-heading bug is no
// longer needed.
function buildPaginationGroups(blocks: HTMLElement[]): PaginationGroup[] {
    const groups: PaginationGroup[] = [];
    let i = 0;
    while (i < blocks.length) {
        if (isStickyPrecedingBlock(blocks[i])) {
            let endIdx = i;
            const cap = Math.min(i + MAX_STICKY_LOOKBACK, blocks.length - 1);
            for (let j = i + 1; j <= cap; j++) {
                if (isStickyPrecedingBlock(blocks[j])) break; // next heading starts a new group
                endIdx = j;
                if (GROUP_END_TAGS.has(blocks[j].tagName)) break; // group terminates AT this block
            }
            groups.push({ startIdx: i, endIdx });
            i = endIdx + 1;
        } else {
            groups.push({ startIdx: i, endIdx: i });
            i++;
        }
    }
    return groups;
}

// Augmented range type used internally to track natural content height for
// trailing-empty-range pruning. The _contentHeight field is stripped before
// returning to callers.
interface PageRangeInternal extends PageRange {
    _contentHeight: number;
}

// Debug logging — tagged so you can filter in DevTools (`[PDF planPages]`).
// Enabled in dev only; never ships verbose output to production.
const PLAN_PAGES_DEBUG = process.env.NODE_ENV !== 'production';
function plog(...args: any[]) {
    if (PLAN_PAGES_DEBUG) console.log('[PDF planPages]', ...args);
}
function plogGroup(label: string) {
    if (PLAN_PAGES_DEBUG) console.group(`[PDF planPages] ${label}`);
}
function plogGroupEnd() {
    if (PLAN_PAGES_DEBUG) console.groupEnd();
}
function describeBlock(b: HTMLElement): string {
    const slateType = b.getAttribute('data-slate-type') || '';
    const tag = b.tagName;
    const text = (b.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60);
    const label = slateType ? `${tag}.${slateType}` : tag;
    return text ? `${label} "${text}${(b.textContent || '').length > 60 ? '…' : ''}"` : label;
}

// Detect a "visually empty" block — an empty paragraph the editor renders to
// give the caret a landing spot, but which produces no real content in the PDF.
// We treat as empty: no non-whitespace text, no images, no tables, no embeds.
// Used to trim trailing emptiness so the PDF doesn't get a blank tail page.
function isVisuallyEmptyBlock(el: HTMLElement): boolean {
    const text = (el.textContent || '').trim();
    if (text.length > 0) return false;
    // Any media / structural descendant means "not empty"
    if (el.querySelector('img, video, table, figure, iframe, hr, [data-doc-id], [data-slate-type="image"], [data-slate-type="embed"]')) {
        return false;
    }
    return true;
}

function planPages(contentEl: HTMLElement, defaultPageHeightCSS: number): { ranges: PageRange[]; edits: MarginEdit[] } {
    const edits: MarginEdit[] = [];
    const internalRanges: PageRangeInternal[] = [];

    const rawBlocks = Array.from(contentEl.children).filter(
        (c) => c instanceof HTMLElement && c.getAttribute('data-slate-node') === 'element',
    ) as HTMLElement[];

    // Trim trailing visually-empty blocks BEFORE pagination so a final empty
    // paragraph (TrailingBlockPlugin's caret-landing spot, or a few stray empty
    // `<p>`s the user left at the bottom) doesn't get its own PDF page.
    // We don't trim from the middle — only the tail — because a blank line in
    // the middle of the document is intentional spacing.
    // ALSO `display: none` the trimmed blocks: they're filtered out of the
    // `blocks` list for pagination, but `contentEl.offsetHeight` still includes
    // them otherwise — and the rasterized canvas captures the full content
    // height. Hiding shrinks the canvas so the PDF doesn't get a tail of
    // whitespace. `edits` carries the restore so this is fully reversible.
    let trimmedTail = 0;
    const blocks = [...rawBlocks];
    while (blocks.length > 0 && isVisuallyEmptyBlock(blocks[blocks.length - 1])) {
        const empty = blocks.pop()!;
        edits.push({ el: empty, previous: empty.style.marginTop, previousDisplay: empty.style.display });
        empty.style.display = 'none';
        trimmedTail++;
    }
    // Force layout flush after hiding so subsequent offsetTop/offsetHeight
    // reads reflect the shrunk contentEl.
    if (trimmedTail > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        contentEl.offsetHeight;
    }

    let pageTop = 0;
    let hasBlockOnCurrentPage = false;
    let currentPageContentEnd = 0;

    plogGroup(`run start`);
    plog('defaultPageHeightCSS', Math.round(defaultPageHeightCSS), 'px');
    plog('contentEl total height', Math.round(contentEl.offsetHeight), 'px');
    plog('total blocks', rawBlocks.length, `(${blocks.length} after trimming ${trimmedTail} trailing empty)`);
    plog('PAGE_TOP_PADDING_CSS', PAGE_TOP_PADDING_CSS, 'PAGE_BOTTOM_PADDING_CSS', PAGE_BOTTOM_PADDING_CSS);
    plogGroupEnd();

    // If after trimming there's nothing to paginate, emit a single empty page
    // and return. The slice loop downstream tolerates a single empty range
    // (it'll render a blank PDF page, but the document had zero content so
    // that's the only honest output).
    if (blocks.length === 0) {
        return { ranges: [{ topCSS: 0, heightCSS: defaultPageHeightCSS }], edits };
    }

    const blockBottom = (b: HTMLElement) => b.offsetTop + b.offsetHeight;

    const pushBlockToY = (block: HTMLElement, targetTop: number, naturalTop: number) => {
        // The block's `offsetTop` (naturalTop) already includes its CSS
        // margin-top. Setting `style.marginTop = pushPx` OVERRIDES the CSS
        // margin entirely, so the block would land at
        //   preceding_sibling.bottom + pushPx
        // = (naturalTop - cssMarginTop) + pushPx
        // = targetTop - cssMarginTop
        // i.e. cssMarginTop pixels above its intended position. For an h2
        // with margin-top: 20px that's a ~20px shift that causes the top
        // of the heading to bleed onto the previous page and the table
        // below it to start mid-row on the next page (the user-visible
        // "table split across pages" bug). Restore the CSS margin by
        // adding it back into the inline override.
        const computed = window.getComputedStyle(block);
        const cssMarginTop = parseFloat(computed.marginTop) || 0;
        const pushPx = targetTop - naturalTop;
        edits.push({ el: block, previous: block.style.marginTop });
        block.style.marginTop = `${pushPx + cssMarginTop}px`;
        // Force a layout flush so subsequent `offsetTop` reads in the loop
        // reflect the push immediately, not after the next idle tick.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        block.offsetHeight;
    };

    // Build groups first so the main loop operates on sticky units rather than
    // individual blocks. This eliminates the old findStickyGroupStart /
    // canPullGroup logic and fixes the orphaned-heading-first-on-page bug.
    const groups = buildPaginationGroups(blocks);

    plogGroup(`groups built (${groups.length} groups from ${blocks.length} blocks)`);
    if (PLAN_PAGES_DEBUG) {
        groups.forEach((g, idx) => {
            const head = blocks[g.startIdx];
            const tail = blocks[g.endIdx];
            const h = tail.offsetTop + tail.offsetHeight - head.offsetTop;
            const tags = blocks
                .slice(g.startIdx, g.endIdx + 1)
                .map((b) => b.tagName)
                .join('+');
            plog(`group#${idx} blocks[${g.startIdx}..${g.endIdx}] (${tags}) height=${Math.round(h)}px head=${describeBlock(head)}`);
        });
    }
    plogGroupEnd();

    let currentPageIdx = 0;
    for (let gi = 0; gi < groups.length; gi++) {
        const group = groups[gi];
        const firstBlock = blocks[group.startIdx];
        const lastBlock = blocks[group.endIdx];

        // Skip zero-height blocks (hidden or collapsed).
        if (firstBlock.offsetHeight <= 0) {
            plog(`group#${gi} SKIP (zero height) ${describeBlock(firstBlock)}`);
            continue;
        }

        const naturalTop = firstBlock.offsetTop;
        // Re-read after any previous push flush — lastBlock may have moved.
        const naturalEnd = lastBlock.offsetTop + lastBlock.offsetHeight;
        const groupHeight = naturalEnd - naturalTop;
        const pageDefaultBottom = pageTop + defaultPageHeightCSS;

        // Change 3: strict fit test subtracts bottom padding so content
        // never packs against the absolute page bottom edge.
        const strictFitBottom = pageTop + defaultPageHeightCSS - PAGE_BOTTOM_PADDING_CSS;

        plogGroup(
            `group#${gi} → page#${currentPageIdx} | h=${Math.round(groupHeight)}px | naturalTop=${Math.round(
                naturalTop,
            )} naturalEnd=${Math.round(naturalEnd)} | pageTop=${Math.round(pageTop)} strictBottom=${Math.round(
                strictFitBottom,
            )} | ${describeBlock(firstBlock)}`,
        );

        if (!hasBlockOnCurrentPage || naturalEnd <= strictFitBottom) {
            // Group fits on the current page within the bottom-padding budget.
            const reason = !hasBlockOnCurrentPage ? 'first block on page' : 'fits within strict bottom';
            plog(`✓ STRICT-FIT (${reason}) — currentPageContentEnd now ${Math.round(naturalEnd)}`);
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
        } else if (groupHeight <= defaultPageHeightCSS - PAGE_TOP_PADDING_CSS - PAGE_BOTTOM_PADDING_CSS) {
            // Group fits on a page by itself (not a tall block). Decide whether
            // to force-fit it into the current page's bottom-padding zone or
            // push it to a new page.

            // Change 2: backstop force-fit — if pushing would leave a large
            // empty gap (> 30 % of the page) AND the group only slightly
            // overflows the strict boundary (within the bottom-padding budget),
            // absorb it rather than creating a nearly-empty page.
            const emptyIfPushed = pageDefaultBottom - currentPageContentEnd;
            const overflowAmount = naturalEnd - pageDefaultBottom;
            const looseFit =
                hasBlockOnCurrentPage && emptyIfPushed > 0.3 * defaultPageHeightCSS && overflowAmount <= PAGE_BOTTOM_PADDING_CSS;

            plog(
                `decision needed: emptyIfPushed=${Math.round(emptyIfPushed)} (${Math.round(
                    (emptyIfPushed / defaultPageHeightCSS) * 100,
                )}%) overflow=${Math.round(overflowAmount)}px backstopLimit=${PAGE_BOTTOM_PADDING_CSS}px`,
            );

            if (looseFit) {
                // Force-fit into the bottom-padding zone — treat as fitting.
                plog(`✓ LOOSE-FIT (backstop: gap > 30%, overflow within budget) — absorbed into current page`);
                currentPageContentEnd = naturalEnd;
            } else {
                // Push the whole group (heading + body) to the next page.
                // Because the group already includes its heading, there is no
                // separate "pull" step — firstBlock IS the heading (or the
                // only block in a standalone group).
                const naturalPageHeight = currentPageContentEnd - pageTop + PAGE_BOTTOM_PADDING_CSS;
                const pageHeight = Math.max(naturalPageHeight, defaultPageHeightCSS);
                internalRanges.push({ topCSS: pageTop, heightCSS: pageHeight, _contentHeight: currentPageContentEnd - pageTop });

                plog(
                    `→ PUSH to new page | committed page#${currentPageIdx}: top=${Math.round(pageTop)} height=${Math.round(
                        pageHeight,
                    )} naturalContent=${Math.round(naturalPageHeight)} (gap=${Math.round(pageHeight - naturalPageHeight)}px on this page)`,
                );

                const newPageTop = pageTop + pageHeight;
                const newPageContentTop = newPageTop + PAGE_TOP_PADDING_CSS;

                pushBlockToY(firstBlock, newPageContentTop, naturalTop);
                pageTop = newPageTop;
                // Re-read from DOM after the push flush.
                currentPageContentEnd = blockBottom(lastBlock);
                hasBlockOnCurrentPage = true;
                currentPageIdx++;
                plog(`  new page#${currentPageIdx} starts at ${Math.round(pageTop)}, content end now ${Math.round(currentPageContentEnd)}`);
            }
        } else {
            // Group is too tall for any single default-height page — extend the
            // current page to fit it. Tall tables stay together (no row split)
            // by growing the page rather than slicing mid-block. Heading
            // immediately above this block is already on the current page, so
            // it stays glued.
            // Edge case: pageTop === 0 and first group is tall → page extends,
            // same as before.
            plog(
                `⤢ EXTEND page (group taller than usable page: ${Math.round(groupHeight)}px > ${Math.round(
                    defaultPageHeightCSS - PAGE_TOP_PADDING_CSS - PAGE_BOTTOM_PADDING_CSS,
                )}px usable)`,
            );
            currentPageContentEnd = naturalEnd;
            hasBlockOnCurrentPage = true;
        }
        plogGroupEnd();
    }

    if (hasBlockOnCurrentPage) {
        const naturalPageHeight = currentPageContentEnd - pageTop + PAGE_BOTTOM_PADDING_CSS;
        const finalHeight = Math.max(naturalPageHeight, defaultPageHeightCSS);
        internalRanges.push({ topCSS: pageTop, heightCSS: finalHeight, _contentHeight: currentPageContentEnd - pageTop });
        plog(
            `final page#${currentPageIdx}: top=${Math.round(pageTop)} height=${Math.round(finalHeight)} naturalContent=${Math.round(
                naturalPageHeight,
            )} (gap=${Math.round(finalHeight - naturalPageHeight)}px)`,
        );
    } else {
        internalRanges.push({ topCSS: 0, heightCSS: defaultPageHeightCSS, _contentHeight: 0 });
        plog(`no blocks rendered — emitting one empty default-size page`);
    }

    // Change 4: drop trailing near-empty ranges (content height below threshold).
    // These arise when pushBlockToY math leaves a tiny tail range with effectively
    // no content — e.g. a forced-push that moved everything to the previous page.
    const EMPTY_RANGE_THRESHOLD = PAGE_TOP_PADDING_CSS + 24;
    let droppedTrailing = 0;
    while (internalRanges.length > 1) {
        const last = internalRanges[internalRanges.length - 1];
        if (last._contentHeight < EMPTY_RANGE_THRESHOLD) {
            internalRanges.pop();
            droppedTrailing++;
        } else {
            break;
        }
    }
    if (droppedTrailing > 0) plog(`dropped ${droppedTrailing} trailing near-empty page(s) (< ${EMPTY_RANGE_THRESHOLD}px content)`);

    plogGroup(`done — ${internalRanges.length} pages`);
    internalRanges.forEach((r, i) => {
        plog(
            `page#${i}: top=${Math.round(r.topCSS)} height=${Math.round(r.heightCSS)} contentHeight=${Math.round(
                r._contentHeight,
            )} gap=${Math.round(r.heightCSS - r._contentHeight - PAGE_BOTTOM_PADDING_CSS)}px`,
        );
    });
    plogGroupEnd();

    // Strip internal field before returning — callers only expect PageRange.
    const ranges: PageRange[] = internalRanges.map(({ topCSS, heightCSS }) => ({ topCSS, heightCSS }));

    return { ranges, edits };
}

function restoreMarginEdits(edits: MarginEdit[]) {
    for (const e of edits) {
        e.el.style.marginTop = e.previous;
        if (e.previousDisplay !== undefined) {
            e.el.style.display = e.previousDisplay;
        }
    }
}

export async function exportEditorToPdf({ target, filename = 'document', singlePage = false }: ExportToPdfOptions) {
    const [{ default: html2canvas }, PDFLib] = await Promise.all([import('html2canvas-pro'), import('pdf-lib')]);

    const contentEl =
        (target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null) ??
        (target.querySelector('[contenteditable]') as HTMLElement | null) ??
        target;

    const backups = expandForCapture(target);
    const style = document.createElement('style');
    document.head.append(style);

    // Wait for fonts before measurement. If Gilroy (or any web font) is
    // still loading when planPages reads block heights, subsequent reflow
    // shifts content vertically by 1-3px per line — across a long doc that
    // accumulates into a full-row offset, causing tables to start mid-row
    // on the next PDF page. document.fonts.ready resolves once all
    // currently-pending font loads finish.
    if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
        try {
            await (document as any).fonts.ready;
        } catch {
            /* font loading is best-effort */
        }
    }

    // Pre-fetch images as base64 to bypass CORS on S3 presigned URLs.
    const [docIdBase64Map, otherImagesMap] = await Promise.all([resolveDocIdImagesToBase64(contentEl), preloadImagesAsDataUrls(contentEl)]);

    // Wait for every <img> currently in the live editor to FINISH decoding
    // before we measure block heights. Without this, `block.offsetHeight`
    // can return the placeholder height (the <img>'s naturalHeight is 0
    // until the bytes are decoded), planPages plans pages too tight, and
    // html2canvas later snapshots with the fully-decoded images at their
    // real heights — pushing subsequent in-cell content past where the
    // PDF slice ends. The classic symptom: image renders but the text in
    // the same cell beneath/beside it is gone.
    await Promise.all(
        Array.from(contentEl.querySelectorAll('img')).map((img) => {
            const node = img as HTMLImageElement;
            // .decode() resolves when the image has been fully decoded and is
            // ready to paint. Use .complete as a fast path; fall through to
            // a load promise for the rest. Errors are swallowed — a broken
            // image still has a placeholder height we can measure.
            if (node.complete && node.naturalHeight > 0) return Promise.resolve();
            return (
                node.decode
                    ? node.decode()
                    : new Promise<void>((resolve) => {
                          const done = () => resolve();
                          node.addEventListener('load', done, { once: true });
                          node.addEventListener('error', done, { once: true });
                      })
            ).catch(() => undefined);
        }),
    );
    // Force a layout flush so block.offsetTop / offsetHeight reflect the
    // post-decode state before planPages reads them.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    contentEl.offsetHeight;
    const defaultPageHeightCSS = getDefaultPageHeightPx(contentEl.offsetWidth || contentEl.scrollWidth || 1229);
    // singlePage mode: bypass planPages entirely — one range covering the
    // whole editor scrollHeight. `edits` stays empty so restoreMarginEdits
    // is a no-op. The slice loop below produces one PDF page sized to the
    // full canvas; no widow/orphan logic, no marginTop pushes, no sticky
    // heading handling — there are no page boundaries to defend against.
    const { ranges, edits } = singlePage
        ? { ranges: [{ topCSS: 0, heightCSS: contentEl.scrollHeight }] as PageRange[], edits: [] as MarginEdit[] }
        : planPages(contentEl, defaultPageHeightCSS);

    // Collect link rects AFTER pagination so positions reflect the adjusted
    // layout (a link in a pushed block ends up at its new Y).
    // Force a layout pass before measuring — the marginTop edits we just
    // applied need to flush before getBoundingClientRect / offsetTop are
    // accurate.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    contentEl.offsetHeight;
    const links = collectLinks(contentEl);

    // Compute the largest Y we plan to slice from + a safety buffer. We use
    // MAX(scrollHeight, last_range.bottom + 200) so html2canvas has enough
    // vertical room even if (a) the clone reflows slightly taller than the
    // live editor or (b) the planned last page is taller than scrollHeight
    // because it was clamped to the default min. The buffer is cheap —
    // unused canvas height is just white pixels — but truncation here
    // silently drops content from the bottom of the PDF.
    const plannedBottomCSS = ranges.length > 0 ? ranges[ranges.length - 1].topCSS + ranges[ranges.length - 1].heightCSS : 0;
    const windowHeightCSS = Math.max(contentEl.scrollHeight, plannedBottomCSS) + 200;
    // Captured INSIDE onclone (the cloned element is destroyed after
    // html2canvas finishes, so we can't measure it after the fact). We
    // compare to the live editor's scrollHeight and warn loudly if they
    // disagree by more than `CLONE_HEIGHT_DRIFT_TOLERANCE_PX`. A large
    // drift = the clone reflowed differently from the live editor (font
    // not loaded, CSS mismatch, etc.), and silent truncation is likely.
    const CLONE_HEIGHT_DRIFT_TOLERANCE_PX = 50;
    let clonedScrollHeight = 0;

    let canvas: HTMLCanvasElement;
    try {
        canvas = await html2canvas(contentEl, {
            backgroundColor: '#ffffff',
            scale: SNAPSHOT_SCALE,
            useCORS: true,
            allowTaint: false,
            windowHeight: windowHeightCSS,
            windowWidth: contentEl.scrollWidth,
            onclone: (doc: Document) => {
                const editorElement = doc.querySelector('[data-testid="plate-editor-content"]') ?? contentEl;
                if (editorElement) {
                    // Strip the page-card chrome (border + rounded corners +
                    // drop-shadow) from the snapshot. Those styles exist so the
                    // editor visually reads as a "sheet of paper" against the
                    // gray canvas in the live UI, but they have no business in
                    // the exported PDF — html2canvas-pro otherwise rasterises
                    // them as a thin gray line + soft shadow around every page.
                    (editorElement as HTMLElement).style.border = 'none';
                    (editorElement as HTMLElement).style.borderRadius = '0';
                    (editorElement as HTMLElement).style.boxShadow = 'none';

                    // Suppress the cell-selection blue tint inside the snapshot.
                    // The .cellSelected class paints a `::after` overlay; we
                    // also strip the class below, but CSS-module hashing makes
                    // class-name matching fragile across builds — a style tag
                    // overrides the pseudo-element regardless of the hash.
                    // Same defense for the image "selected" ring.
                    const overrideStyle = doc.createElement('style');
                    overrideStyle.textContent = `
                        [class*="cellSelected"]::after,
                        [class*="cell-selected"]::after {
                            display: none !important;
                            background: transparent !important;
                            content: none !important;
                        }
                        [class*="imageContainerSelected"] {
                            box-shadow: none !important;
                            outline: none !important;
                        }
                    `;
                    doc.head.appendChild(overrideStyle);

                    // Bullet/highlight rasterization fix — html2canvas-pro paints `::marker` and
                    // `<mark>` bg against the line-height box; pin them to glyph baseline for export only.
                    const rasterFixStyle = doc.createElement('style');
                    rasterFixStyle.textContent = `
                        /* PDF export — pin bullet markers to text baseline */
                        ul, ol {
                            line-height: 1.6 !important;
                        }
                        ul > li::marker,
                        ol > li::marker {
                            font-size: 1em !important;
                            color: #37352f;
                        }
                        ul[style*='list-style-type: square'] > li::before {
                            top: 0.5em !important;
                        }
                        /* PDF export — anchor highlight background tightly to text.
                         * Covers both Plate's HighlightPlugin (<mark>) AND
                         * FontBackgroundColorPlugin (inline <span style="background-color:…">).
                         * box-decoration-break: clone is load-bearing — without it,
                         * html2canvas-pro drops the glyphs from preceding line-fragments
                         * when a highlighted span wraps across multiple visual lines
                         * (bg paints fine, text disappears). With clone, each line
                         * fragment is treated as its own decorated box and rasterizes
                         * its glyphs correctly. */
                        mark,
                        span[style*="background-color"] {
                            padding: 0.05em 0 !important;
                            background-clip: content-box;
                            -webkit-box-decoration-break: clone;
                            box-decoration-break: clone;
                        }
                    `;
                    doc.head.appendChild(rasterFixStyle);

                    editorElement.querySelectorAll('img').forEach((img) => {
                        const docId = img.getAttribute('data-doc-id');
                        const orig = img.getAttribute('src') || (img as HTMLImageElement).src;
                        // Prefer the docId-resolved base64 (fresh presigned →
                        // fetch). Fall back to whatever the raw-URL preloader
                        // managed to grab.
                        const dataUrl = (docId ? docIdBase64Map.get(docId) : null) || (orig ? otherImagesMap.get(orig) : null);
                        if (dataUrl) img.setAttribute('src', dataUrl);
                    });

                    // Strip editor chrome that leaks into the PDF snapshot:
                    //   - the × delete button + the 4 corner resize handles
                    //     (rendered by ImageElement only when the image is
                    //     currently "selected" in the editor — html2canvas
                    //     captures whatever is in the live DOM)
                    //   - the blue selection outline (set via box-shadow on
                    //     `.imageContainerSelected`)
                    // Both are pure editor UI and have no business in the
                    // exported PDF. CSS modules hash class names so we match
                    // by substring rather than the literal class string.
                    editorElement
                        .querySelectorAll<HTMLElement>('[class*="ImageElement_deleteBtn"], [class*="ImageElement_handle"]')
                        .forEach((el) => el.remove());
                    editorElement.querySelectorAll<HTMLElement>('[class*="ImageElement_imageContainerSelected"]').forEach((el) => {
                        // Drop the selected-state class so its box-shadow
                        // doesn't draw a blue ring around the image.
                        el.className = el.className
                            .split(/\s+/)
                            .filter((cls) => !/ImageElement_imageContainerSelected/.test(cls))
                            .join(' ');
                    });

                    // Strip table cell-selection chrome — multi-cell drag-select
                    // adds an `rgba(35,131,226,0.08)` overlay via a `::after`
                    // pseudo-element on the `cellSelected` class. The class
                    // also paints when the caret rests on a cell edge during
                    // resize, which is when the user notices the blue tint
                    // leaking into the PDF. CSS modules hash the class name
                    // (e.g. `tableElements_cellSelected__abc`), match by
                    // substring.
                    editorElement.querySelectorAll<HTMLElement>('[class*="tableElements_cellSelected"]').forEach((el) => {
                        el.className = el.className
                            .split(/\s+/)
                            .filter((cls) => !/tableElements_cellSelected/.test(cls))
                            .join(' ');
                    });
                    // Same treatment for the table-level `tableSelecting` class,
                    // which suppresses carets but is irrelevant in the PDF.
                    editorElement.querySelectorAll<HTMLElement>('[class*="tableElements_tableSelecting"]').forEach((el) => {
                        el.className = el.className
                            .split(/\s+/)
                            .filter((cls) => !/tableElements_tableSelecting/.test(cls))
                            .join(' ');
                    });
                    // Resize handles render only on hover but stay in DOM with
                    // pointer-events listeners; their visible 1-px hover line
                    // (::after) can paint into the snapshot if the user's
                    // Remove editor-only resize handles.
                    editorElement
                        .querySelectorAll<HTMLElement>(
                            '[class*="tableElements_colHandle"], [class*="tableElements_rowHandle"], [class*="tableElements_leftHandle"]',
                        )
                        .forEach((el) => el.remove());

                    // Force combined text-decoration inline — html2canvas can override CSS selectors.
                    editorElement.querySelectorAll<HTMLElement>('[data-slate-leaf="true"]').forEach((leaf) => {
                        let hasU = false;
                        let hasS = false;
                        const all = [leaf, ...Array.from(leaf.querySelectorAll<HTMLElement>('*'))];
                        let p: HTMLElement | null = leaf.parentElement;
                        while (p && p !== editorElement) {
                            all.push(p);
                            p = p.parentElement;
                        }
                        for (const el of all) {
                            const tag = el.tagName.toLowerCase();
                            if (tag === 'u') hasU = true;
                            if (tag === 's' || tag === 'del') hasS = true;
                            if (hasU && hasS) break;
                        }
                        if (hasU && hasS) {
                            const existing = leaf.getAttribute('style') || '';
                            leaf.setAttribute(
                                'style',
                                `${existing}; text-decoration: underline line-through !important; text-decoration-line: underline line-through !important;`,
                            );
                        }
                    });
                    // Do NOT override font-family — different widths cause clone height drift and page truncation.

                    // Measure clone height for drift detection (truncation warning).
                    clonedScrollHeight = (editorElement as HTMLElement).scrollHeight || 0;
                }
            },
        });
    } finally {
        style.remove();
        restoreMarginEdits(edits);
        restoreAfterCapture(backups);
    }

    // Warn if clone height drifted — page boundaries may truncate content.
    const liveScrollHeight = contentEl.scrollHeight;
    const drift = clonedScrollHeight - liveScrollHeight;
    if (Math.abs(drift) > CLONE_HEIGHT_DRIFT_TOLERANCE_PX) {
        // eslint-disable-next-line no-console
        console.warn(`[exportToPdf] clone scrollHeight drifted ${drift}px from live editor`);
    }

    const pdfDoc = await PDFLib.PDFDocument.create();
    const PDFName = PDFLib.PDFName;
    const PDFString = PDFLib.PDFString;

    const pxToPt = PDF_CONTENT_WIDTH_PT / canvas.width;

    for (const range of ranges) {
        const sliceTopCanvas = Math.max(0, Math.floor(range.topCSS * SNAPSHOT_SCALE));
        // Use planned height even if canvas is shorter (keeps last page full-size).
        const plannedBottomCanvas = Math.ceil((range.topCSS + range.heightCSS) * SNAPSHOT_SCALE);
        const sliceBottomCanvas = Math.min(canvas.height, plannedBottomCanvas);
        const sliceHeightCanvas = Math.max(1, sliceBottomCanvas - sliceTopCanvas);
        const plannedPageHeightCanvas = Math.max(1, plannedBottomCanvas - sliceTopCanvas);

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightCanvas;
        const ctx = sliceCanvas.getContext('2d');
        if (!ctx) break;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, -sliceTopCanvas);

        const png = await pdfDoc.embedPng(sliceCanvas.toDataURL('image/png'));
        const drawHpt = sliceHeightCanvas * pxToPt;
        const plannedHpt = plannedPageHeightCanvas * pxToPt;
        const pageHeightPt = plannedHpt + MARGIN_PT * 2;
        const page = pdfDoc.addPage([PAGE_WIDTH_PT, pageHeightPt]);
        // PDF coords are origin-bottom-left.
        const imageBottomPt = pageHeightPt - MARGIN_PT - drawHpt;
        page.drawImage(png, {
            x: MARGIN_PT,
            y: imageBottomPt,
            width: PDF_CONTENT_WIDTH_PT,
            height: drawHpt,
        });

        const pageLinks = links.filter((l) => l.y + l.h > sliceTopCanvas && l.y < sliceBottomCanvas);
        if (pageLinks.length) {
            const annotRefs: any[] = [];
            for (const l of pageLinks) {
                const topInSlice = l.y - sliceTopCanvas;
                const x1Pt = MARGIN_PT + l.x * pxToPt;
                const x2Pt = x1Pt + l.w * pxToPt;
                // PDF y is bottom-left origin.
                const yTopPt = imageBottomPt + drawHpt - topInSlice * pxToPt;
                const y1Pt = yTopPt - l.h * pxToPt;
                const y2Pt = yTopPt;
                const annot = pdfDoc.context.register(
                    pdfDoc.context.obj({
                        Type: 'Annot',
                        Subtype: 'Link',
                        Rect: [x1Pt, y1Pt, x2Pt, y2Pt],
                        Border: [0, 0, 0],
                        A: pdfDoc.context.obj({
                            Type: 'Action',
                            S: 'URI',
                            URI: PDFString.of(l.url),
                        }),
                    }),
                );
                annotRefs.push(annot);
            }
            (page.node as any).set(PDFName.of('Annots'), pdfDoc.context.obj(annotRefs));
        }
    }

    const pdfBase64 = await pdfDoc.saveAsBase64({ dataUri: true });

    const link = document.createElement('a');
    link.href = pdfBase64;
    link.download = `${filename}.pdf`;
    document.body.append(link);
    link.click();
    link.remove();
}
